import { Box, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import ProSelectField from "../Inputs/SelectField";
import { fieldTruePermission } from "../../../utils/formHelper";
import ProButton from "../ProButton";
import { FileDownload } from "@mui/icons-material";
import { useFormContext } from "react-hook-form";
import axios from "axios";
import { BASE_API_V2_URI } from "../../../utils/constant";

import { subModPermissionSelector } from "../../store/features/permissions/permissionsSlice";
import { useSelector } from "react-redux";
import { R_ALL_DOCUMENTS, R_DOCUMENTS_671_FORM } from "../../../utils/permissionReferenceConfig";

export default function ComplianceNavigatorHeader({ formProps, formConfig, typeOptions, selectedCards }) {

  const { canExport: canExport671 } = useSelector(subModPermissionSelector(R_ALL_DOCUMENTS, R_DOCUMENTS_671_FORM));


  const { getValues } = useFormContext();

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const COMPLIANCE_NAVIGATOR_DOWNLOAD = 'complianceNavigator/download';

  // eventually response with content-disposition header filename attribute
  function buildFilename(selectedCards) {
    const now = new Date();

    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');

    const timestamp = `${yyyy}${mm}${dd}_${hh}${min}${ss}`;

    if (!selectedCards || selectedCards.length === 0) {
      return `ComplianceDocuments_${timestamp}.zip`; // fallback
    }

    // selectedCards is a string here
    const selectedArray = selectedCards.split(',').map(s => s.trim());

    const documentTypesPart = selectedArray
      .map(doc => doc.replace(/\s+/g, '')) // remove any spaces
      .join('_'); // join with underscores

    return `${documentTypesPart}_${timestamp}.zip`;
  }

  function getDocumentTypes(selectedCards) {
    const documentTypeMappings = {
      "671": "671",
      "Appendix A": "AppendixA",
      "Appendix D": "AppendixD",
      "CPR": "CPR",
      "Davis Bacon Forms": "DavisBaconForm",
      "Journey Worker Certifications": "JourneyWorkerCertificationForm"
    };

    if (!selectedCards || selectedCards.length === 0) return '';

    const selectedArray = selectedCards.map(s => s.trim());

    const mappedTypes = selectedArray
      .map(title => documentTypeMappings[title]) // Only map if known
      .filter(Boolean); // REMOVE undefined or falsy values

    return mappedTypes.join(',');
  }

  const is671Selected = selectedCards.includes("671");
  const hideDownloadButton = is671Selected && !canExport671;


  const downloadZip = async () => {
    const selectedValues = getValues();

    setIsDownloading(true);  // Start loading
    setDownloadComplete(false);

    try {
      // selectedCards is an array here
      const documentTypes = getDocumentTypes(selectedCards);

      const projectId = selectedValues.projectId || 'All';
      const partnerId = selectedValues.partnerId || 'All';

      axios.defaults.baseURL = BASE_API_V2_URI;
      const response = await axios.get(
        COMPLIANCE_NAVIGATOR_DOWNLOAD,
        {
          params: { documentTypes, projectId, partnerId },
          responseType: "blob",
          headers: {
            Accept: "application/zip"
          }
        });

      const downloadUrl = window.URL.createObjectURL(response.data);

      // eventually get filename from the content-disposition header
      const filename = buildFilename(documentTypes);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);

      setDownloadComplete(true); // Mark as complete
      setTimeout(() => {
        setDownloadComplete(false);
      }, 3000); // reset after 3 seconds
    } catch (error) {
      console.error('Error downloading zip:', error);
    } finally {
      setIsDownloading(false); // Stop loading
    }
  };

  return (
    <Box test-data-id ="project-filter-header"
      sx={{
        display: { md: "flex", sm: "block" },
        justifyContent: "space-between",
        marginBottom: "15px",
        width: { md: "100%" }
      }}
    >
      <Box
        sx={{
          display: { md: "flex", sm: "block" },
          width: { sm: "100%", md: "30%" }
        }}
      >
        {Object.keys(typeOptions).map((key) => (
          <Box test-data-id={`project-filter-by-${key}`}
            key={key}
            sx={{
              width: { xs: "100%", md: "50%" },
              mb: { xs: 2, md: 0 },
              mr: { md: 2 }
            }}
          >
            <ProSelectField
              {...formProps}
              value={typeOptions[key]?.[0] ?? ""}
              {...formConfig[key]}
              options={typeOptions[key] ?? []}
              permission={fieldTruePermission}
              name={key}
            />
          </Box>
        ))}
      </Box>
        {!hideDownloadButton && (
          <ProButton onClick={downloadZip} endIcon={<FileDownload />} disabled={isDownloading || selectedCards.length === 0}>
          {isDownloading ? (
            <>
              Preparing download...&nbsp;
              <CircularProgress size={16} color="inherit" />
            </>
          ) : downloadComplete ? (
            "Download ready!"
          ) : (
            "Download Zip"
          )}
        </ProButton>
        )}
    </Box>
  );
}

ComplianceNavigatorHeader.propTypes = {
  formProps: PropTypes.object,
  formConfig: PropTypes.object,
  typeOptions: PropTypes.objectOf(PropTypes.array),
};